/**
 * Reopen targets for a prioritization session — the one step "back" the
 * server's state machine allows from each state.
 *
 * Mirrors transition_session_state (mig 735, live on staging + prod):
 *   DRAFT              -> CRITERIA_FINALIZED | RUN_COMPLETE
 *   CRITERIA_FINALIZED -> DRAFT | RUN_COMPLETE
 *   RUN_COMPLETE       -> CRITERIA_FINALIZED | PUBLISHED
 *   PUBLISHED          -> (nothing)
 *
 * So "reopen" is CRITERIA_FINALIZED -> DRAFT and RUN_COMPLETE ->
 * CRITERIA_FINALIZED; DRAFT has nothing to reopen and PUBLISHED can't be
 * reopened. Aiming every reopen at DRAFT (the old behavior) was refused by
 * the server from RUN_COMPLETE.
 */
import type { SessionState } from '@/types/prioritization/session';

const REOPEN_TARGET: Record<SessionState, SessionState | null> = {
  DRAFT: null,
  CRITERIA_FINALIZED: 'DRAFT',
  RUN_COMPLETE: 'CRITERIA_FINALIZED',
  PUBLISHED: null,
};

export function getSessionReopenTarget(state: SessionState): SessionState | null {
  return REOPEN_TARGET[state] ?? null;
}

/** Why a session in this state cannot be reopened (null when it can). */
export function getSessionReopenRefusal(state: SessionState): string | null {
  if (state === 'PUBLISHED') return 'A published session cannot be reopened.';
  if (state === 'DRAFT') return 'This session is already a draft.';
  return getSessionReopenTarget(state) ? null : `A session in ${state} cannot be reopened.`;
}

export const SESSION_REOPEN_LABEL: Partial<Record<SessionState, string>> = {
  DRAFT: 'Reopen to Draft',
  CRITERIA_FINALIZED: 'Reopen to criteria finalized',
};
